/**
 * Query Provider
 *
 * Configures and provides TanStack React Query, optionally with an
 * IndexedDB-persisted cache (stale-while-revalidate across page loads).
 */

import { type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from 'idb-keyval';
import { env } from '@shared/config';
import { DataverseError } from '@shared/api';

// =============================================================================
// Query Client Configuration
// =============================================================================

/**
 * Create query client with appropriate defaults
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: 5 minutes
        staleTime: 5 * 60 * 1000,
        // Cache time: 30 minutes
        gcTime: 30 * 60 * 1000,
        // Retry configuration
        retry: (failureCount, error) => {
          // Don't retry on 4xx client errors (except 429 rate-limit, which is transient)
          if (error instanceof DataverseError) {
            if (error.status >= 400 && error.status < 500 && error.status !== 429) {
              return false;
            }
          }
          // Retry up to 3 times for server errors, 429, and network errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus in production
        refetchOnWindowFocus: env.isProduction,
        // Don't refetch on mount if data is fresh
        refetchOnMount: true,
      },
      mutations: {
        // No automatic retry: most creates here are not idempotent, and a
        // create whose response was lost to the network has usually already
        // committed, so a replay makes a duplicate row. A mutation that is
        // safe to retry opts in on its own useMutation.
        retry: false,
      },
    },
  });
}

// Create singleton client
const queryClient = createQueryClient();

// =============================================================================
// Persistence (opt-in via persistScope)
// =============================================================================

/** Bump to invalidate every persisted cache (e.g. when an entity mapper's
 *  output shape changes in a way stale rows would break). */
const PERSIST_BUSTER = '2026-06-11.1';

/** How long restored data may be served before it's discarded outright.
 *  Restored queries are older than staleTime, so they refetch in the
 *  background regardless — this only bounds how old a first paint can be. */
const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;

/**
 * Only persist query families whose data is known to be plain JSON
 * (Dataverse entity mappers return strings/numbers/booleans only). Queries
 * holding class instances or Date objects would deserialize subtly wrong, so
 * persistence is allowlist-based. These are the heavy org-wide pulls that
 * dominate Ops first-paint (My Groups / Search / Group pages).
 */
const PERSISTED_KEY_ROOTS = new Set([
  'opsGroups',
  'contractYears',
  'groupSolutions',
  'groupServices',
  'groupServiceOptions',
  'giInstances',
  'giFields',
  'giCategories',
  'catalogSolutionsForSearch',
]);

function buildPersister(scope: string) {
  const idbKey = `vera-rq-cache:${scope}`;
  return createAsyncStoragePersister({
    key: idbKey,
    storage: {
      getItem: (key: string) => idbGet<string>(key).then((v) => v ?? null),
      setItem: (key: string, value: string) => idbSet(key, value),
      removeItem: (key: string) => idbDel(key),
    },
    throttleTime: 2000,
  });
}

const PERSIST_KEY_PREFIX = 'vera-rq-cache:';

/**
 * Delete every persisted query cache from IndexedDB. Called on logout so a
 * signed-out user's org-wide data doesn't linger on a shared machine. The
 * in-memory client is also dropped on the logout page reload; this clears the
 * on-disk copy. User-scoped keys already prevent cross-user reads — this is
 * defense-in-depth for the same-user-logs-out case.
 */
export async function clearPersistedQueryCache(): Promise<void> {
  try {
    const all = await idbKeys();
    await Promise.all(
      all
        .filter((k): k is string => typeof k === 'string' && k.startsWith(PERSIST_KEY_PREFIX))
        .map((k) => idbDel(k)),
    );
  } catch {
    // Best-effort — never block logout on cache cleanup.
  }
}

// =============================================================================
// Provider Component
// =============================================================================

interface QueryProviderProps {
  children: ReactNode;
  /**
   * When set, the query cache is persisted to IndexedDB under this scope and
   * restored on the next load (stale-while-revalidate: last visit's data
   * paints immediately while fresh data refetches in the background).
   * Include anything that changes what the data *means* — e.g. the active
   * Dataverse org — so a dev-org cache is never shown against prod.
   */
  persistScope?: string;
}

/**
 * Query Provider component
 * Provides React Query context and optional DevTools
 */
export function QueryProvider({ children, persistScope }: QueryProviderProps) {
  if (!persistScope) {
    return (
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: buildPersister(persistScope),
        buster: PERSIST_BUSTER,
        maxAge: PERSIST_MAX_AGE,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.state.status === 'success' &&
            typeof query.queryKey[0] === 'string' &&
            PERSISTED_KEY_ROOTS.has(query.queryKey[0]),
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}

// Export for direct access if needed
export { queryClient };
